import { Injectable } from '@angular/core';

import {
  Action,
  Selector,
  State,
  StateContext,
  StateToken,
  Store
} from '@ngxs/store';
import { catchError, tap, throwError } from 'rxjs';
import { AppErrorCode } from 'src/app/errors/enums/app-error-code.enum';
import { AppErrorNavigationService } from 'src/app/errors/services/app-error-navigation.service';

import {
  LoginAction,
  LogoutAction,
  RefreshTokenAction,
  RemoveAccessTokenAction,
  RemoveGuestKeyAction,
  SessionExpiredAction,
  SetProfessorAction
} from './session.actions';
import { ProfessorModel } from './session.model';
import { LivErrorResponse } from '../../models/liv-response-protocol.model';
import { AuthService } from '../../services/requests/auth.service';
import { SetUserCountryAction } from '../user/user.actions';

export interface SessionStateModel {
  accessToken: string | null;
  refreshToken: string | null;
  guestKey: string | null;
  professor: ProfessorModel | null;
  identifier: string | null;
}

const SESSION_STATE_DEFAULTS: SessionStateModel = {
  accessToken: null,
  refreshToken: null,
  guestKey: null,
  professor: null,
  identifier: null
};

const SESSION_STATE_TOKEN = new StateToken<SessionStateModel>('session');

@State<SessionStateModel>({
  name: SESSION_STATE_TOKEN,
  defaults: SESSION_STATE_DEFAULTS
})
@Injectable()
export class SessionState {
  constructor(
    private store: Store,
    private authService: AuthService,
    private appErrorNavigation: AppErrorNavigationService
  ) {}

  @Selector([SessionState])
  static session(state: SessionStateModel | undefined) {
    return {
      accessToken: state?.accessToken ?? null,
      refreshToken: state?.refreshToken ?? null,
      guestKey: state?.guestKey ?? null
    };
  }

  @Selector([SessionState])
  static accessToken(state: SessionStateModel | undefined) {
    return state?.accessToken ?? null;
  }

  @Selector([SessionState])
  static refreshToken(state: SessionStateModel | undefined) {
    return state?.refreshToken ?? null;
  }

  @Selector([SessionState])
  static guestKey(state: SessionStateModel | undefined) {
    return state?.guestKey ?? null;
  }

  @Selector([SessionState])
  static isAuthenticated(state: SessionStateModel | undefined) {
    return !!state?.accessToken;
  }

  @Selector([SessionState])
  static professor(
    state: SessionStateModel | undefined
  ): SessionStateModel['professor'] {
    return state?.professor ?? null;
  }

  @Selector([SessionState])
  static identifier(state: SessionStateModel | undefined) {
    return state?.identifier ?? null;
  }

  @Action(RemoveAccessTokenAction) removeAccessToken({
    patchState
  }: StateContext<SessionStateModel>): void {
    patchState({
      accessToken: null
    });
  }

  @Action(RemoveGuestKeyAction) removeGuestKey({
    patchState
  }: StateContext<SessionStateModel>): void {
    patchState({
      guestKey: null
    });
  }

  @Action(SetProfessorAction) setProfessor(
    { patchState }: StateContext<SessionStateModel>,
    { payload }: SetProfessorAction
  ): void {
    patchState({
      professor: payload
    });
  }

  @Action(LoginAction) login(
    { patchState, setState }: StateContext<SessionStateModel>,
    { payload }: LoginAction
  ) {
    if (!payload.guestKey) {
      throw new Error('Guest Key is required');
    }

    return this.authService.validateGuestKey(payload.guestKey).pipe(
      catchError((error: LivErrorResponse) => {
        setState({ ...SESSION_STATE_DEFAULTS });
        return throwError(() => error);
      }),
      tap(
        ({
          id,
          id_externo_professor,
          apelido,
          email,
          jwt,
          refresh_token,
          uuid,
          country
        }) => {
          const storedId = this.store.selectSnapshot(SessionState.identifier);
          patchState({
            accessToken: jwt,
            refreshToken: refresh_token,
            guestKey: payload.guestKey,
            identifier: storedId || uuid,
            professor: {
              id,
              externalProfessorId: id_externo_professor,
              username: apelido,
              email
            }
          });

          this.store.dispatch(new SetUserCountryAction(country ?? null));
        }
      )
    );
  }

  @Action(RefreshTokenAction, {
    cancelUncompleted: true
  })
  refreshToken({ getState, patchState }: StateContext<SessionStateModel>) {
    const { refreshToken } = getState() ?? SESSION_STATE_DEFAULTS;

    if (!refreshToken) {
      this.store.dispatch(new SessionExpiredAction());
      return throwError(() => new Error('Refresh Token is required'));
    }

    return this.authService.refreshAuthentication(refreshToken).pipe(
      tap(({ jwt, refreshToken }) => {
        patchState({
          accessToken: jwt,
          refreshToken
        });
      })
    );
  }

  @Action(LogoutAction) logout({ setState }: StateContext<SessionStateModel>) {
    setState({ ...SESSION_STATE_DEFAULTS });

    void this.appErrorNavigation.navigate(AppErrorCode.Session);
  }

  @Action(SessionExpiredAction)
  sessionExpired({ setState }: StateContext<SessionStateModel>) {
    setState({ ...SESSION_STATE_DEFAULTS });
  }
}
